import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { RoomImageVariant } from "@shared/schema";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

export function RoomVariantsDialog({ roomId, roomName, propertyId, readOnly, onClose, onPreview, restoreFocus }: {
  roomId: number | null;
  roomName: string;
  propertyId: number;
  readOnly: boolean;
  onClose: () => void;
  onPreview: (url: string, trigger: HTMLElement) => void;
  restoreFocus: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const deleting = useRef(false);
  const query = useQuery<RoomImageVariant[]>({
    queryKey: ["/api/app/rooms", roomId, "variants"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/app/rooms/${roomId}/variants`);
      return res.json();
    },
    enabled: roomId !== null,
  });
  const remove = useMutation({
    mutationFn: async ({ roomId: id, variantId }: { roomId: number; variantId: number }) => {
      await apiRequest("DELETE", `/api/app/rooms/${id}/variants/${variantId}`, {});
    },
    onSuccess: (_, { roomId: id, variantId }) => {
      queryClient.setQueryData<RoomImageVariant[]>(["/api/app/rooms", id, "variants"], old =>
        old?.filter(variant => variant.id !== variantId));
      queryClient.invalidateQueries({ queryKey: ["/api/app/rooms", id, "variants"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "rooms"] });
      toast({ title: "Variant deleted" });
    },
    onError: (error: Error) => toast({
      title: "Failed to delete variant",
      description: parseErrorMessage(error, "The variant was not deleted. Please try again."),
      variant: "destructive",
    }),
    onSettled: () => { deleting.current = false; },
  });
  const promote = useMutation({
    mutationFn: async ({ roomId: id, variantId }: { roomId: number; variantId: number }) => {
      await apiRequest("POST", `/api/app/rooms/${id}/variants/${variantId}/promote`, {});
    },
    onSuccess: (_, { roomId: id }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "rooms"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/rooms", id, "variants"] });
      toast({ title: "Set as primary image" });
    },
    onError: (error: Error) => toast({
      title: "Failed to set primary",
      description: parseErrorMessage(error, "Couldn't set this as the primary image. Please try again."),
      variant: "destructive",
    }),
  });
  const busy = remove.isPending || promote.isPending;
  // Keep the root mounted during close animations so Radix can release its
  // portal, pointer-event lock and focus scope before the next opening.
  return (
    <Dialog open={roomId !== null} onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" onCloseAutoFocus={event => {
        event.preventDefault();
        restoreFocus();
      }}>
        <DialogHeader>
          <DialogTitle>Image Variants — {roomName}</DialogTitle>
          <DialogDescription>Preview saved images, set a primary image, or delete a variant.</DialogDescription>
        </DialogHeader>
        {query.isLoading ? <Skeleton className="h-20 mt-4" /> : query.isError ? (
          <div role="alert">Couldn't load variants. <button onClick={() => query.refetch()} className="text-brand underline">Try again</button></div>
        ) : !query.data?.length ? (
          <p className="text-sm text-gray-500 mt-4">No saved variants yet. Generate an image and click &quot;Save Variant&quot;.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 mt-4">
            {query.data.map(variant => (
              <div key={variant.id} className="border rounded-lg overflow-hidden" data-testid={`card-variant-${variant.id}`}>
                {variant.outputImageUrl ? (
                  <button type="button" className="w-full" aria-label={`Preview variant ${variant.variantIndex}`} onClick={event => onPreview(variant.outputImageUrl!, event.currentTarget)}>
                    <img src={variant.outputImageUrl} alt="Variant" className="w-full aspect-video object-cover" />
                  </button>
                ) : <div className="w-full aspect-video bg-gray-100 flex items-center justify-center text-xs text-gray-400">No image</div>}
                <div className="p-2 flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-500">Variant #{variant.variantIndex}</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => roomId !== null && promote.mutate({ roomId, variantId: variant.id })}
                      disabled={busy || readOnly || !variant.outputImageUrl}
                      className="text-xs text-brand font-medium disabled:opacity-40" data-testid={`button-promote-variant-${variant.id}`}>Set as primary</button>
                    <button onClick={() => {
                      if (roomId === null || deleting.current || busy || readOnly) return;
                      deleting.current = true;
                      remove.mutate({ roomId, variantId: variant.id });
                    }} disabled={busy || readOnly}
                      className="text-xs text-red-400 hover:text-red-600 disabled:opacity-40" data-testid={`button-delete-variant-${variant.id}`}>
                      {remove.isPending && remove.variables?.variantId === variant.id ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
